/**
 * Table Column Definitions for Sessions Tab
 *
 * Defines 6 columns for displaying MULTI_FILE_VENDOR_ASSESSMENT sessions:
 * 1. Session Title (with SHARED badge)
 * 2. Vendor Product
 * 3. Status (with icon and color)
 * 4. Last Updated (relative time)
 * 5. Created By (avatar + name)
 * 6. Actions (dropdown with Continue/View + Download options)
 */

import type { ZTableColumnProps } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from "@nextui-org/react";
import { Ellipsis } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import ZAvatarGradient from "@/shared/components/avatarGradient";
import { agentSessionStatusLabelMap } from "@/modules/sessions/constants";
import { AgentSessionStatus } from "@/modules/sessions/types";
import type { SessionDetails } from "../types/sessions";

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Get columns for Sessions table
 *
 * @param loadingRows - Record of session IDs with loading state (for per-row spinners)
 * @param handleContinueSession - Handler for Continue/View action
 * @param handleDownloadReport - Handler for Download actions
 * @returns Array of column definitions
 */
export const getSessionsColumns = (
  loadingRows: Record<string, boolean>,
  handleContinueSession: (session: SessionDetails) => void,
  handleDownloadReport: (session: SessionDetails, format?: string) => void
): ZTableColumnProps<SessionDetails>[] => [
  // Column 1: Session Title with SHARED badge
  {
    type: "custom",
    fieldName: "name",
    headerName: "Session Title",
    renderCell: ({ name, permissions, created_by_id }) => (
      <div className="text-[#333] flex gap-2 break-all items-center min-w-[200px]">
        {name ?? ""}
        {permissions && permissions.owner !== created_by_id && (
          <span className="h-3 flex items-center justify-center text-ellipsis overflow-hidden rounded border-1 border-[#006FEE] bg-[#006FEE33] p-1 text-[8px] leading-3 text-[#006FEE] shrink-0">
            SHARED
          </span>
        )}
      </div>
    ),
  },

  // Column 2: Vendor Product
  {
    type: "custom",
    fieldName: "vendor_product",
    headerName: "Vendor Product",
    renderCell: ({ vendor_name, product_name }) => {
      const vendorPart = vendor_name || '';
      const productPart = product_name ? ` ${product_name}` : '';
      const displayText = `${vendorPart}${productPart}`.trim() || 'N/A';

      return (
        <div className="text-[#333] flex gap-[6px] items-center">
          {displayText}
        </div>
      );
    },
  },

  // Column 3: Status with icon and color
  {
    type: "custom",
    fieldName: "status",
    headerName: "Status",
    renderCell: ({ status }) => {
      const statusConfig = agentSessionStatusLabelMap[status];

      return (
        <div className="flex items-center gap-3">
          <Chip
            variant="bordered"
            radius="full"
            className="h-6 border-1.5"
            classNames={{
              content: "px-1 py-2 flex items-center gap-1",
            }}
            style={{
              color: statusConfig?.borderColor ?? "black",
              borderColor: statusConfig?.borderColor ?? "black",
              backgroundColor: statusConfig?.backgroundColor ?? "transparent",
            }}
          >
            {statusConfig?.renderIcon?.()}
          </Chip>
          <div className="text-[#333]">
            {statusConfig?.label ?? "N/A"}
          </div>
        </div>
      );
    },
  },

  // Column 4: Last Updated (relative time)
  {
    type: "custom",
    fieldName: "updated_at",
    headerName: "Last Updated",
    renderCell: ({ updated_at }) => {
      if (dayjs(updated_at).isValid()) {
        const parsedDate = dayjs.utc(updated_at);
        const localDate = parsedDate.local();
        return <div className="text-[#333]">{localDate.fromNow()}</div>;
      }
      return <div className="text-[#333]">N/A</div>;
    },
  },

  // Column 5: Created By with avatar
  {
    type: "custom",
    fieldName: "created_by",
    headerName: "Created By",
    renderCell: ({ created_by }) => {
      return created_by && created_by !== "N/A" ? (
        <div className="flex items-center gap-3">
          <ZAvatarGradient name={created_by} size={24} textSize={10} />
          <p className="font-medium">{created_by}</p>
        </div>
      ) : (
        <div className="text-[#333]">N/A</div>
      );
    },
  },

  // Column 6: Actions dropdown
  {
    type: "custom",
    fieldName: "actions",
    headerName: "",
    renderCell: (row) => {
      const isRowLoading = loadingRows[row.id];
      const isComplete = row.status === AgentSessionStatus.COMPLETE;
      const canDownload = row.final_report_available;

      return (
        <Dropdown>
          <DropdownTrigger>
            <Button
              isIconOnly
              variant="light"
              size="sm"
              isDisabled={isRowLoading}
              className="min-w-unit-8 w-unit-8 h-unit-8"
            >
              <Ellipsis className="h-5 w-5" />
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Session actions">
            <DropdownItem
              key="continue"
              onClick={() => handleContinueSession(row)}
              className="text-[#333]"
            >
              {isComplete ? "View Session" : "Continue Session"}
            </DropdownItem>
            <DropdownItem
              key="download-summary"
              isDisabled={!canDownload}
              onClick={() => canDownload && handleDownloadReport(row, 'summary_pdf')}
              className="text-[#333]"
            >
              Download Summary
            </DropdownItem>
            <DropdownItem
              key="download-full"
              isDisabled={!canDownload}
              onClick={() => canDownload && handleDownloadReport(row)}
              className="text-[#333]"
            >
              Download Full Report
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      );
    },
  },
];
